// src/components/settings/shared/LayoutDisplaySection.jsx
import React, { useState } from 'react';
import { 
  Layout, 
  LayoutGrid,
  Layers,
  Eye, 
  EyeOff, 
  GripVertical, 
  Grid, 
  Image as ImageIcon,
  List, 
  Columns, 
  Maximize2,
  Store,
  Check,
  Minimize2,
  Settings2 
} from 'lucide-react';
import { FormField, SelectInput, Toggle } from './FormComponents';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const LAYOUT_OPTIONS = {
  profile: {
    modern: {
      name: 'Modern',
      description: 'Clean, card-based layout with hero section',
      icon: Layout,
      preview: '/previews/profile-modern.jpg',
      structure: 'hero-cards'
    },
    classic: {
      name: 'Classic',
      description: 'Traditional resume-style layout',
      icon: List,
      preview: '/previews/profile-classic.jpg',
      structure: 'sidebar-content'
    },
    portfolio: {
      name: 'Portfolio',
      description: 'Visual-focused with gallery emphasis',
      icon: Grid,
      preview: '/previews/profile-portfolio.jpg',
      structure: 'masonry-grid'
    },
    minimal: {
      name: 'Minimal',
      description: 'Clean, typography-focused design',
      icon: Maximize2,
      preview: '/previews/profile-minimal.jpg',
      structure: 'single-column'
    }
  },
  booking: {
    calendar: {
      name: 'Calendar View',
      description: 'Large calendar with booking details',
      icon: Layout,
      preview: '/previews/booking-calendar.jpg',
      structure: 'calendar-primary'
    },
    list: {
      name: 'List View',
      description: 'Time slots in list format',
      icon: List,
      preview: '/previews/booking-list.jpg',
      structure: 'list-primary'
    },
    cards: {
      name: 'Card View',
      description: 'Service cards with booking buttons',
      icon: Grid,
      preview: '/previews/booking-cards.jpg',
      structure: 'card-grid'
    },
    sidebar: {
      name: 'Sidebar Layout',
      description: 'Info sidebar with booking form',
      icon: Columns,
      preview: '/previews/booking-sidebar.jpg',
      structure: 'sidebar-form'
    }
  },
  event: {
    hero: {
      name: 'Hero Event',
      description: 'Large hero image with event details',
      icon: Layout,
      preview: '/previews/event-hero.jpg',
      structure: 'hero-details'
    },
    timeline: {
      name: 'Timeline',
      description: 'Event schedule in timeline format',
      icon: List,
      preview: '/previews/event-timeline.jpg',
      structure: 'timeline-primary'
    },
    gallery: {
      name: 'Gallery Focus',
      description: 'Photo gallery with event info',
      icon: Grid,
      preview: '/previews/event-gallery.jpg',
      structure: 'gallery-primary'
    },
    compact: {
      name: 'Compact',
      description: 'Dense layout with key information',
      icon: Maximize2,
      preview: '/previews/event-compact.jpg',
      structure: 'compact-info'
    }
  },
  album: {
    masonry: {
      name: 'Masonry Grid',
      description: 'Pinterest-style photo layout',
      icon: Grid,
      preview: '/previews/album-masonry.jpg',
      structure: 'masonry-grid'
    },
    gallery: {
      name: 'Gallery View',
      description: 'Traditional photo gallery',
      icon: Layout,
      preview: '/previews/album-gallery.jpg',
      structure: 'gallery-grid'
    },
    slideshow: {
      name: 'Slideshow',
      description: 'Full-width slideshow format',
      icon: Maximize2,
      preview: '/previews/album-slideshow.jpg',
      structure: 'slideshow-primary'
    },
    story: {
      name: 'Story Layout',
      description: 'Narrative layout with captions',
      icon: List,
      preview: '/previews/album-story.jpg',
      structure: 'story-format'
    }
  },
  catalog: {
    header: { 
      label: 'Header', 
      description: 'Branding and search bar', 
      required: true 
    },
    hero: { 
      label: 'Hero Banner', 
      description: 'Optional banner/announcement area',
      required: false 
    },
    filters: { 
      label: 'Filter Panel', 
      description: 'Category and price filters',
      required: false 
    },
    items: { 
      label: 'Items Grid', 
      description: 'Main items display',
      required: true 
    },
    footer: { 
      label: 'Footer', 
      description: 'Contact and company info',
      required: false 
    }
  },
  institution: {
    academic: {
      name: 'Academic Focus',
      description: 'Programs and academics prominent',
      icon: Layout,
      preview: '/previews/institution-academic.jpg',
      structure: 'academic-primary'
    },
    campus: {
      name: 'Campus Life',
      description: 'Student life and activities focus',
      icon: Grid,
      preview: '/previews/institution-campus.jpg',
      structure: 'campus-primary'
    },
    professional: {
      name: 'Professional',
      description: 'Business-focused layout',
      icon: Columns,
      preview: '/previews/institution-professional.jpg',
      structure: 'professional-layout'
    },
    community: {
      name: 'Community',
      description: 'Community and engagement focus',
      icon: List,
      preview: '/previews/institution-community.jpg',
      structure: 'community-layout'
    }
  },

  ecommerce: {
    header: { 
      label: 'Store Header', 
      description: 'Store branding, cart, and navigation', 
      required: true 
    },
    hero: { 
      label: 'Hero Section', 
      description: 'Featured banner or promotional area',
      required: false 
    },
    categories: { 
      label: 'Category Navigation', 
      description: 'Product category navigation bar',
      required: false 
    },
    featured: { 
      label: 'Featured Products', 
      description: 'Highlighted products section',
      required: false 
    },
    promos: { 
      label: 'Promotions', 
      description: 'Special offers and deals',
      required: false 
    },
    filters: { 
      label: 'Filter Sidebar', 
      description: 'Product filtering and search',
      required: false,
      variants: ['left', 'right', 'top', 'collapsible', 'hidden']
    },
    products: { 
      label: 'Product Grid', 
      description: 'Main product display area',
      required: true,
      variants: ['2-column', '3-column', '4-column', 'list', 'masonry']
    },
    sidebar: { 
      label: 'Content Sidebar', 
      description: 'Additional widgets and content',
      required: false,
      variants: ['left', 'right', 'none']
    },
    recommendations: { 
      label: 'Recommendations', 
      description: 'Suggested or related products',
      required: false 
    },
    reviews: { 
      label: 'Reviews Section', 
      description: 'Customer reviews and testimonials',
      required: false 
    },
    footer: { 
      label: 'Store Footer', 
      description: 'Footer navigation and information',
      required: true,
      variants: ['minimal', 'standard', 'extended']
    }
  }
};

const CATALOG_LAYOUT_PRESETS = {
  standard: {
    name: 'Standard',
    description: 'Traditional listing layout',
    icon: Grid,
    sections: {
      header: { visible: true },
      hero: { visible: false },
      filters: { visible: true },
      items: { visible: true },
      footer: { visible: true }
    }
  },
  minimal: {
    name: 'Minimal',
    description: 'Clean, simple listing',
    icon: Minimize2,
    sections: {
      header: { visible: true },
      hero: { visible: false },
      filters: { visible: false },
      items: { visible: true },
      footer: { visible: false }
    }
  },
  featured: {
    name: 'Featured',
    description: 'With banner highlight',
    icon: Image,
    sections: {
      header: { visible: true },
      hero: { visible: true },
      filters: { visible: true },
      items: { visible: true },
      footer: { visible: true }
    }
  }
};

const ECOMMERCE_LAYOUT_PRESETS = {
  fullStore: {
    name: 'Full Store',
    description: 'Complete eCommerce layout',
    icon: Store,
    sections: {
      header: { visible: true, variant: 'sticky' },
      hero: { visible: true, variant: 'fullscreen' },
      categories: { visible: true },
      featured: { visible: true },
      promos: { visible: true },
      filters: { visible: true, variant: 'left' },
      products: { visible: true, variant: '3-column' },
      sidebar: { visible: true, variant: 'right' },
      recommendations: { visible: true },
      reviews: { visible: true },
      footer: { visible: true, variant: 'extended' }
    }
  },
  minimal: {
    name: 'Minimal Store',
    description: 'Clean, focused shopping',
    icon: Minimize2,
    sections: {
      header: { visible: true, variant: 'minimal' },
      hero: { visible: false },
      categories: { visible: true },
      featured: { visible: false },
      promos: { visible: false },
      filters: { visible: true, variant: 'collapsible' },
      products: { visible: true, variant: '3-column' },
      sidebar: { visible: false },
      recommendations: { visible: false },
      reviews: { visible: false },
      footer: { visible: true, variant: 'minimal' }
    }
  },
  showcase: {
    name: 'Product Showcase',
    description: 'Visual-focused store',
    icon: Image,
    sections: {
      header: { visible: true, variant: 'transparent' },
      hero: { visible: true, variant: 'carousel' },
      categories: { visible: true },
      featured: { visible: true },
      promos: { visible: true },
      filters: { visible: true, variant: 'top' },
      products: { visible: true, variant: 'masonry' },
      sidebar: { visible: false },
      recommendations: { visible: true },
      reviews: { visible: true },
      footer: { visible: true, variant: 'standard' }
    }
  }
};

const SECTION_DEFINITIONS = {
  profile: {
    hero: { label: 'Hero Section', description: 'Main banner with photo and intro', required: true },
    about: { label: 'About', description: 'Personal or professional overview' },
    experience: { label: 'Experience', description: 'Work history and positions' },
    education: { label: 'Education', description: 'Academic background' },
    skills: { label: 'Skills', description: 'Technical and soft skills' },
    projects: { label: 'Portfolio', description: 'Featured work and projects' },
    achievements: { label: 'Achievements', description: 'Awards and recognitions' },
    testimonials: { label: 'Testimonials', description: 'Client and colleague reviews' },
    services: { label: 'Services', description: 'Services offered' },
    pricing: { label: 'Pricing', description: 'Service pricing and packages' },
    gallery: { label: 'Gallery', description: 'Photo and media gallery' },
    contact: { label: 'Contact', description: 'Contact form and information', required: true }
  },
  booking: {
    hero: { label: 'Hero Section', description: 'Main banner with booking info', required: true },
    services: { label: 'Services', description: 'Available booking services' },
    calendar: { label: 'Calendar', description: 'Booking calendar interface', required: true },
    pricing: { label: 'Pricing', description: 'Service pricing and rates' },
    policies: { label: 'Policies', description: 'Booking terms and policies' },
    reviews: { label: 'Reviews', description: 'Customer reviews and ratings' },
    faq: { label: 'FAQ', description: 'Frequently asked questions' },
    location: { label: 'Location', description: 'Venue or service location' },
    gallery: { label: 'Gallery', description: 'Photos of services/venue' },
    contact: { label: 'Contact', description: 'Contact information' }
  },
  event: {
    hero: { label: 'Hero Section', description: 'Event banner and key details', required: true },
    details: { label: 'Event Details', description: 'Date, time, location information' },
    description: { label: 'Description', description: 'Event description and agenda' },
    rsvp: { label: 'RSVP', description: 'Registration and RSVP form' },
    schedule: { label: 'Schedule', description: 'Event timeline and program' },
    speakers: { label: 'Speakers', description: 'Featured speakers or hosts' },
    gallery: { label: 'Gallery', description: 'Event photos and media' },
    location: { label: 'Location', description: 'Venue details and directions' },
    sponsors: { label: 'Sponsors', description: 'Event sponsors and partners' },
    contact: { label: 'Contact', description: 'Event organizer contact' }
  },
  album: {
    header: { label: 'Album Header', description: 'Album title and description', required: true },
    gallery: { label: 'Photo Gallery', description: 'Main photo collection', required: true },
    categories: { label: 'Categories', description: 'Photo categories and filters' },
    featured: { label: 'Featured Photos', description: 'Highlighted photos' },
    slideshow: { label: 'Slideshow', description: 'Automatic photo slideshow' },
    download: { label: 'Download Options', description: 'Photo download functionality' },
    sharing: { label: 'Social Sharing', description: 'Share and social features' },
    comments: { label: 'Comments', description: 'Photo comments and interactions' },
    metadata: { label: 'Photo Info', description: 'EXIF and photo information' },
    search: { label: 'Search', description: 'Photo search functionality' }
  },
  catalog: {
    header: { label: 'Store Header', description: 'Store branding and navigation', required: true },
    featured: { label: 'Featured Products', description: 'Highlighted products' },
    categories: { label: 'Categories', description: 'Product categories', required: true },
    products: { label: 'Product Grid', description: 'Main product display', required: true },
    filters: { label: 'Filters', description: 'Product filtering options' },
    search: { label: 'Search', description: 'Product search functionality' },
    cart: { label: 'Shopping Cart', description: 'Cart and checkout process' },
    reviews: { label: 'Reviews', description: 'Product reviews and ratings' },
    recommendations: { label: 'Recommendations', description: 'Suggested products' },
    footer: { label: 'Footer', description: 'Store footer information' }
  },

  ecommerce: {
    header: { label: 'Store Header', description: 'Store branding and navigation', required: true },
    hero: { label: 'Hero Section', description: 'Featured banner or promotional area', required: false },
    categories: { 
      label: 'Category Navigation', 
      description: 'Product category navigation bar',
      required: false 
    },
    filters: { label: 'Filter Sidebar', description: 'Product filtering and search', required: false },
    products: { label: 'Product Grid', description: 'Main product display area', required: true },
    search: { label: 'Search', description: 'Product search functionality' },
    cart: { label: 'Shopping Cart', description: 'Cart and checkout process' },
    reviews: { label: 'Reviews', description: 'Product reviews and ratings' },
    recommendations: { label: 'Recommendations', description: 'Suggested products' },
    footer: { label: 'Footer', description: 'Store footer information' }
  },
  institution: {
    hero: { label: 'Hero Section', description: 'Institution banner and main info', required: true },
    about: { label: 'About', description: 'Institution overview and mission' },
    leadership: { label: 'Leadership', description: 'Leadership team and staff' },
    programs: { label: 'Academic Programs', description: 'Courses and programs offered' },
    events: { label: 'Events', description: 'Upcoming events and calendar' },
    news: { label: 'News & Updates', description: 'Latest news and announcements' },
    resources: { label: 'Resources', description: 'Documents and educational resources' },
    galleries: { label: 'Photo Galleries', description: 'Campus and activity photos' },
    admissions: { label: 'Admissions', description: 'Admission process and requirements' },
    contact: { label: 'Contact', description: 'Contact information and form', required: true }
  },

  organisation: {
    hero: { name: 'Hero Section', description: 'Main banner and introduction', required: true },
    about: { name: 'About Us', description: 'Organisation description', required: true },
    leadership: { name: 'Leadership', description: 'Team and leadership', required: false },
    services: { name: 'Services', description: 'What we offer', required: false },
    events: { name: 'Events', description: 'Upcoming events', required: false },
    news: { name: 'News', description: 'Latest news and updates', required: false },
    contact: { name: 'Contact', description: 'Contact information', required: true }
  }
};

// Sortable Item Component
const SortableItem = ({ sectionId, section, isVisible, isRequired, onToggle, config, onVariantChange }) => {
  const [showVariants, setShowVariants] = useState(false);
  
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: sectionId });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const hasVariants = section.variants && section.variants.length > 0;
  const currentVariant = config?.variant;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`border border-gray-200 rounded-lg bg-white transition-shadow ${
        isDragging ? 'shadow-lg' : ''
      }`}
    >
      {/* Main Section Row */}
      <div className="flex items-center gap-4 p-4">
        <div {...attributes} {...listeners} className="cursor-move">
          <GripVertical className="h-5 w-5 text-gray-400" />
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h5 className="font-medium text-gray-900">{section.label}</h5>
            {isRequired && (
              <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full">
                Required
              </span>
            )}
            {hasVariants && isVisible && (
              <span className="text-xs px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full">
                {currentVariant || 'default'}
              </span>
            )}
          </div>
          <p className="text-sm text-gray-500 mt-1">{section.description}</p>
        </div>
        
        <div className="flex items-center gap-2">
          {/* Variant Toggle Button */}
          {hasVariants && isVisible && (
            <button
              onClick={() => setShowVariants(!showVariants)}
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
              title="Configure variants"
            >
              <Settings2 className="h-4 w-4 text-gray-600" />
            </button>
          )}
          
          {/* Visibility Toggle */}
          <button
            onClick={() => onToggle(sectionId)}
            disabled={isRequired}
            className={`p-2 rounded-lg transition-colors ${
              isVisible && !isRequired
                ? 'bg-green-100 text-green-600 hover:bg-green-200'
                : isRequired
                ? 'bg-blue-100 text-blue-600 cursor-not-allowed opacity-50'
                : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
            }`}
            title={isRequired ? 'Required element' : isVisible ? 'Hide section' : 'Show section'}
          >
            {isVisible ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
          </button>
        </div>
      </div>

      {/* Variant Options Panel */}
      {showVariants && hasVariants && isVisible && (
        <div className="px-4 pb-4 border-t border-gray-100 bg-gray-50">
          <div className="pt-4">
            <label className="block text-xs font-medium text-gray-700 mb-2 uppercase tracking-wide">
              Display Variant
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {section.variants.map((variant) => (
                <button
                  key={variant}
                  onClick={() => onVariantChange(sectionId, variant)}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                    currentVariant === variant
                      ? 'bg-indigo-600 text-white shadow-sm'
                      : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
                  }`}
                >
                  {variant}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const PresetSelector = ({ currentPreset, onSelectPreset, templateType }) => {
  // Choose the right preset based on template type
  const presets = templateType === 'ecommerce' 
    ? ECOMMERCE_LAYOUT_PRESETS 
    : templateType === 'catalog'
    ? CATALOG_LAYOUT_PRESETS
    : {};

  if (Object.keys(presets).length === 0) {
    return null; // No presets for this template type
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {Object.entries(presets).map(([key, preset]) => (
        <button
          key={key}
          onClick={() => onSelectPreset(key)}
          className={`p-4 rounded-xl border-2 text-left transition-all ${
            currentPreset === key
              ? 'border-indigo-600 bg-indigo-50'
              : 'border-gray-200 hover:border-gray-300 bg-white'
          }`}
        >
          <div className="flex items-center gap-2 mb-2">
            <h4 className="font-semibold text-gray-900">{preset.name}</h4>
            {currentPreset === key && (
              <Check className="h-4 w-4 text-indigo-600" />
            )}
          </div>
          <p className="text-sm text-gray-600">{preset.description}</p>
        </button>
      ))}
    </div>
  );
};

// Layout Preview Modal Component
const LayoutPreviewModal = ({ layout, sections, previewMode, onClose }) => {
  const getPreviewDimensions = () => {
    switch (previewMode) {
      case 'mobile': return 'w-80 h-[640px]';
      case 'tablet': return 'w-96 h-[512px]';
      default: return 'w-[800px] h-[600px]';
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-6xl max-h-[90vh] overflow-hidden">
        <div className="flex justify-between items-center p-6 border-b">
          <div>
            <h3 className="text-xl font-bold text-gray-900">Layout Preview: {layout.name}</h3>
            <p className="text-gray-600">{previewMode} view</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
          >
            ✕
          </button>
        </div>
        
        <div className="p-6 bg-gray-100 flex justify-center">
          <div className={`${getPreviewDimensions()} bg-white rounded-lg shadow-lg overflow-auto`}>
            <div className="p-4 space-y-4">
              <div className="h-8 bg-gray-200 rounded animate-pulse" />
              {sections.map((sectionId, index) => (
                <div key={sectionId} className="space-y-2">
                  <div className="h-4 bg-gray-300 rounded w-24" />
                  <div className="h-16 bg-gray-100 rounded" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export const LayoutDisplaySection = ({ 
  formData, 
  onInputChange, 
  templateType = 'profile',
  showPreview = true 
}) => {
  const [selectedLayout, setSelectedLayout] = useState(
    formData.layoutType || 
    (LAYOUT_OPTIONS[templateType] && Object.keys(LAYOUT_OPTIONS[templateType]).length > 0 
      ? Object.keys(LAYOUT_OPTIONS[templateType])[0] 
      : 'default')
  );
  const [previewMode, setPreviewMode] = useState('desktop');
  const [showLayoutPreview, setShowLayoutPreview] = useState(false);

  const layouts = LAYOUT_OPTIONS[templateType] || LAYOUT_OPTIONS.profile;
  const sections = SECTION_DEFINITIONS[templateType] || SECTION_DEFINITIONS.profile;

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handlePresetSelect = (presetSections) => {
    // Update section visibility and variants
    const newVisibility = {};
    const newConfig = { ...formData };
    
    Object.entries(presetSections).forEach(([sectionId, config]) => {
      newVisibility[sectionId] = config.visible;
      if (config.variant) {
        newConfig[`${sectionId}Variant`] = config.variant;
      }
    });
    
    onInputChange('sectionVisibility', newVisibility);
    Object.entries(newConfig).forEach(([key, value]) => {
      if (key.includes('Variant')) {
        onInputChange(key, value);
      }
    });
  };

  // NEW: Handle variant change
  const handleVariantChange = (sectionId, variant) => {
    onInputChange(`${sectionId}Variant`, variant);
  };

  const handleLayoutChange = (field, value) => {
    onInputChange(`layoutSettings.${field}`, value); // ✅ Use dot notation
  };

  const handleSectionToggle = (sectionId) => {
    const currentVisibility = formData.sectionVisibility || {};
    onInputChange('sectionVisibility', {
      ...currentVisibility,
      [sectionId]: !currentVisibility[sectionId]
    });
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      const currentOrder = formData.sectionOrder || Object.keys(sections);
      const oldIndex = currentOrder.indexOf(active.id);
      const newIndex = currentOrder.indexOf(over.id);

      onInputChange('sectionOrder', arrayMove(currentOrder, oldIndex, newIndex));
    }
  };

  const getVisibleSections = () => {
    const order = formData.sectionOrder || Object.keys(sections);
    return order.filter(sectionId => sections[sectionId]);
  };

  const currentIntegration = formData.layoutSettings?.headerHeroIntegration;
  const isOverlayMode = currentIntegration === 'overlay';
  const isNormalMode = currentIntegration === 'normal';

  return (
    <div className="space-y-8">
      <div>
        <h3 className="text-2xl font-bold text-gray-900 mb-2">Layout & Display</h3>
        <p className="text-gray-600">
          Choose your layout style and configure which sections to display
        </p>

      </div><PresetSelector
        templateType={templateType}
        currentConfig={formData}
        onSelectPreset={handlePresetSelect}
      />

      {/* Layout Selection */}
      <div className="space-y-6">
        <h4 className="text-lg font-semibold text-gray-900">Layout Style</h4>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {Object.entries(layouts).map(([layoutId, layout]) => {
            const LayoutIcon = layout.icon || Layout;
            const isSelected = selectedLayout === layoutId;
            
            return (
              <div
                key={layoutId}
                onClick={() => handleLayoutChange(layoutId)}
                className={`relative p-4 border-2 rounded-xl cursor-pointer transition-all ${
                  isSelected
                    ? 'border-blue-500 bg-blue-50 shadow-md'
                    : 'border-gray-200 hover:border-gray-300 hover:shadow-sm'
                }`}
              >
                {/* Layout Preview Thumbnail */}
                <div className="aspect-video bg-gray-100 rounded-lg mb-3 flex items-center justify-center">
                  {layout.preview ? (
                    <img
                      src={layout.preview}
                      alt={layout.name}
                      className="w-full h-full object-cover rounded-lg"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  <div className="flex items-center justify-center w-full h-full bg-gray-100 rounded-lg">
                    <LayoutIcon className="h-8 w-8 text-gray-400" />
                  </div>
                </div>
                
                <div className="text-center">
                  <h5 className="font-medium text-gray-900">{layout.name}</h5>
                  <p className="text-sm text-gray-500 mt-1">{layout.description}</p>
                </div>

                {isSelected && (
                  <div className="absolute top-2 right-2 w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                    <div className="w-2 h-2 bg-white rounded-full" />
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Preview Button */}
        {showPreview && (
          <div className="flex items-center gap-4">
            <button
              onClick={() => setShowLayoutPreview(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
            >
              <Eye className="h-4 w-4" />
              Preview Layout
            </button>
            
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">Preview:</span>
              <select
                value={previewMode}
                onChange={(e) => setPreviewMode(e.target.value)}
                className="px-3 py-1 border border-gray-300 rounded-lg text-sm"
              >
                <option value="desktop">Desktop</option>
                <option value="tablet">Tablet</option>
                <option value="mobile">Mobile</option>
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Section Management */}
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h4 className="text-lg font-semibold text-gray-900">Section Management</h4>
          <div className="text-sm text-gray-500">
            {Object.values(formData.sectionVisibility || {}).filter(Boolean).length} of {Object.keys(sections).length} sections visible
          </div>
        </div>

        <DndContext 
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext 
            items={getVisibleSections()}
            strategy={verticalListSortingStrategy}
          >
            <div className="space-y-3">
              {getVisibleSections().map((sectionId) => {
                const section = sections[sectionId];
                if (!section) return null;

                const isVisible = formData.sectionVisibility?.[sectionId] !== false;
                const isRequired = section.required;
                
                return (
                  <SortableItem
                    key={sectionId}
                    sectionId={sectionId}
                    section={section}
                    isVisible={isVisible}
                    isRequired={isRequired}
                    onToggle={handleSectionToggle}
                  />
                );
              })}
            </div>
          </SortableContext>
        </DndContext>
      </div>

      {/* Display Options */}
      <div className="space-y-6">
        <h4 className="text-lg font-semibold text-gray-900">Display Options</h4>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <Toggle
              checked={formData.showSectionNumbers}
              onChange={(value) => onInputChange('showSectionNumbers', value)}
              label="Section Numbers"
              description="Show numbers for each section"
            />

            <Toggle
              checked={formData.enableSectionAnchors}
              onChange={(value) => onInputChange('enableSectionAnchors', value)}
              label="Section Anchors"
              description="Enable direct links to sections"
            />

            <Toggle
              checked={formData.showSectionDividers}
              onChange={(value) => onInputChange('showSectionDividers', value)}
              label="Section Dividers"
              description="Show visual dividers between sections"
            />
          </div>

          <div className="space-y-4">
            <Toggle
              checked={formData.enableSmoothScroll}
              onChange={(value) => onInputChange('enableSmoothScroll', value)}
              label="Smooth Scrolling"
              description="Smooth scroll between sections"
            />

            <Toggle
              checked={formData.showTableOfContents}
              onChange={(value) => onInputChange('showTableOfContents', value)}
              label="Table of Contents"
              description="Show navigation menu for sections"
            />

            <Toggle
              checked={formData.stickyNavigation}
              onChange={(value) => onInputChange('stickyNavigation', value)}
              label="Sticky Navigation"
              description="Keep navigation visible while scrolling"
            />
          </div>
        </div>

        {/* Header & Hero Integration Section */}
        {formData.layoutSettings?.sectionVisibility?.hero !== false && (
          <div className="space-y-6">
            <div>
              <h4 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <Layers className="h-5 w-5 text-indigo-600" />
                Header & Hero Integration
              </h4>
              <p className="text-sm text-gray-600 mt-1">
                Configure how the header interacts with the hero section
              </p>
            </div>
            
            <div className="bg-gradient-to-br from-indigo-50 to-purple-50 border border-indigo-200 rounded-lg p-6">
              <label className="block text-sm font-medium text-gray-700 mb-4">
                Header Position Mode
              </label>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Overlay Option */}
                <button
                  type="button"
                  onClick={() => handleLayoutChange('headerHeroIntegration', 'overlay')}
                  className={`p-4 border-2 rounded-xl text-left transition-all ${
                    isOverlayMode
                      ? 'border-indigo-600 bg-white shadow-lg ring-2 ring-indigo-100'
                      : 'border-gray-200 bg-white hover:border-indigo-300 hover:shadow-md'
                  }`}
                >
                  <div className="flex items-start gap-3 mb-3">
                    <div className={`p-2 rounded-lg ${
                      isOverlayMode ? 'bg-indigo-100' : 'bg-gray-100'
                    }`}>
                      <Layers className={`h-5 w-5 ${
                        isOverlayMode ? 'text-indigo-600' : 'text-gray-600'
                      }`} />
                    </div>
                    <div className="flex-1">
                      <h5 className="font-semibold text-gray-900 mb-1">Overlay Hero</h5>
                      <p className="text-xs text-gray-600">
                        Header floats above hero with transparency
                      </p>
                    </div>
                    {isOverlayMode && (
                      <Check className="h-5 w-5 text-indigo-600 flex-shrink-0" />
                    )}
                  </div>
                  
                  {/* Visual Preview */}
                  <div className="relative h-20 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-lg overflow-hidden">
                    <div className="absolute top-0 left-0 right-0 bg-white/90 backdrop-blur-sm p-2 border-b border-white/20">
                      <div className="flex items-center gap-2">
                        <div className="h-2 w-16 bg-indigo-400 rounded"></div>
                        <div className="ml-auto flex gap-1">
                          <div className="h-2 w-2 bg-indigo-400 rounded-full"></div>
                          <div className="h-2 w-2 bg-indigo-400 rounded-full"></div>
                        </div>
                      </div>
                    </div>
                    <div className="absolute bottom-2 left-2 right-2">
                      <div className="h-2 w-32 bg-white/90 rounded mb-1"></div>
                      <div className="h-1.5 w-24 bg-white/70 rounded"></div>
                    </div>
                  </div>
                </button>

                {/* Normal Flow Option */}
                <button
                  type="button"
                  onClick={() => handleLayoutChange('headerHeroIntegration', 'normal')}
                  className={`p-4 border-2 rounded-xl text-left transition-all ${
                    isNormalMode
                      ? 'border-indigo-600 bg-white shadow-lg ring-2 ring-indigo-100'
                      : 'border-gray-200 bg-white hover:border-indigo-300 hover:shadow-md'
                  }`}
                >
                  <div className="flex items-start gap-3 mb-3">
                    <div className={`p-2 rounded-lg ${
                      isNormalMode ? 'bg-indigo-100' : 'bg-gray-100'
                    }`}>
                      <LayoutGrid className={`h-5 w-5 ${
                        isNormalMode ? 'text-indigo-600' : 'text-gray-600'
                      }`} />
                    </div>
                    <div className="flex-1">
                      <h5 className="font-semibold text-gray-900 mb-1">Normal Flow</h5>
                      <p className="text-xs text-gray-600">
                        Header above hero in standard layout
                      </p>
                    </div>
                    {isNormalMode && (
                      <Check className="h-5 w-5 text-indigo-600 flex-shrink-0" />
                    )}
                  </div>
                  
                  {/* Visual Preview */}
                  <div className="h-20 rounded-lg overflow-hidden border border-gray-200">
                    <div className="bg-white border-b border-gray-200 p-2">
                      <div className="flex items-center gap-2">
                        <div className="h-2 w-16 bg-gray-400 rounded"></div>
                        <div className="ml-auto flex gap-1">
                          <div className="h-2 w-2 bg-gray-400 rounded-full"></div>
                          <div className="h-2 w-2 bg-gray-400 rounded-full"></div>
                        </div>
                      </div>
                    </div>
                    <div className="bg-gradient-to-r from-indigo-500 to-purple-500 p-2 h-full flex flex-col justify-end">
                      <div className="h-2 w-32 bg-white/90 rounded mb-1"></div>
                      <div className="h-1.5 w-24 bg-white/70 rounded"></div>
                    </div>
                  </div>
                </button>
              </div>

              {/* Overlay Customization Options - only show when overlay mode is selected */}
              {isOverlayMode && (
                <div className="mt-6 p-4 bg-white rounded-lg border border-indigo-100">
                  <h6 className="text-sm font-medium text-gray-900 mb-4 flex items-center gap-2">
                    <Settings2 className="h-4 w-4" />
                    Overlay Customization
                  </h6>
                  
                  <div className="space-y-4">
                    {/* Transparent Background Toggle */}
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex-1">
                        <div className="text-sm font-medium text-gray-900">Transparent Background</div>
                        <div className="text-xs text-gray-600 mt-0.5">
                          Make header background transparent over hero
                        </div>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer ml-4">
                        <input
                          type="checkbox"
                          checked={formData.layoutSettings?.headerTransparentOnHero ?? true}
                          onChange={(e) => handleLayoutChange('headerTransparentOnHero', e.target.checked)}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                      </label>
                    </div>

                    {/* Blur Effect Toggle */}
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex-1">
                        <div className="text-sm font-medium text-gray-900">Backdrop Blur</div>
                        <div className="text-xs text-gray-600 mt-0.5">
                          Add glass-morphism blur effect to header
                        </div>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer ml-4">
                        <input
                          type="checkbox"
                          checked={formData.layoutSettings?.headerBlurOnHero ?? true}
                          onChange={(e) => handleLayoutChange('headerBlurOnHero', e.target.checked)}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                      </label>
                    </div>

                    {/* Opacity Slider */}
                    <div className="p-3 bg-gray-50 rounded-lg">
                      <label className="block text-sm font-medium text-gray-900 mb-2">
                        Header Opacity
                      </label>
                      <div className="flex items-center gap-3">
                        <input
                          type="range"
                          min="0"
                          max="100"
                          value={formData.layoutSettings?.headerOpacityOnHero ?? 90}
                          onChange={(e) => handleLayoutChange('headerOpacityOnHero', parseInt(e.target.value))}
                          className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-indigo-600"
                        />
                        <div className="w-14 text-sm font-medium text-gray-900 text-right">
                          {formData.layoutSettings?.headerOpacityOnHero ?? 90}%
                        </div>
                      </div>
                      <p className="text-xs text-gray-500 mt-2">
                        Adjust transparency when header overlays hero section
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Info Box */}
              <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex gap-2">
                  <Eye className="h-4 w-4 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div className="text-xs text-blue-900">
                    <strong>Tip:</strong> Overlay mode works best with hero images or videos. 
                    Normal flow is recommended for text-heavy hero sections.
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Advanced Layout Settings */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <FormField label="Container Width">
            <SelectInput
              value={formData.containerWidth}
              onChange={(value) => onInputChange('containerWidth', value)}
              options={[
                { value: 'full', label: 'Full Width' },
                { value: 'wide', label: 'Wide (1200px)' },
                { value: 'normal', label: 'Normal (1024px)' },
                { value: 'narrow', label: 'Narrow (768px)' }
              ]}
            />
          </FormField>

          <FormField label="Section Spacing">
            <SelectInput
              value={formData.sectionSpacing}
              onChange={(value) => onInputChange('sectionSpacing', value)}
              options={[
                { value: 'tight', label: 'Tight' },
                { value: 'normal', label: 'Normal' },
                { value: 'relaxed', label: 'Relaxed' },
                { value: 'loose', label: 'Loose' }
              ]}
            />
          </FormField>

          <FormField label="Animation Style">
            <SelectInput
              value={formData.animationStyle}
              onChange={(value) => onInputChange('animationStyle', value)}
              options={[
                { value: 'none', label: 'None' },
                { value: 'fade', label: 'Fade In' },
                { value: 'slide', label: 'Slide Up' },
                { value: 'bounce', label: 'Bounce' },
                { value: 'zoom', label: 'Zoom In' }
              ]}
            />
          </FormField>
        </div>
      </div>

      {/* Layout Preview Modal */}
      {showLayoutPreview && (
        <LayoutPreviewModal
          layout={layouts[selectedLayout]}
          sections={getVisibleSections()}
          previewMode={previewMode}
          onClose={() => setShowLayoutPreview(false)}
        />
      )}
    </div>
  );
};

export default LayoutDisplaySection;